import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Minus, X, Star, Award, MessageCircle, ArrowLeft,
  Languages, Trash2, ExternalLink, CheckCircle2, AlertTriangle,
} from "lucide-react";

const LANGS = [
  { k: "en", label: "EN" },
  { k: "hi", label: "हिं" },
  { k: "gu", label: "ગુ" },
];

const tx = (obj, lang, fallback = "") => obj?.[lang] || obj?.en || fallback;

export const CustomerMenu = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [lang, setLang] = useState("en");
  const [activeCat, setActiveCat] = useState(null);
  const [cart, setCart] = useState({}); // { itemId: qty }

  // UI state
  const [view, setView] = useState("menu"); // menu | cart | feedback | loyalty | reward
  const [orderModal, setOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState({ name: "", phone: "" });
  const [feedbackForm, setFeedbackForm] = useState({ name: "", phone: "", rating: 5, message: "" });
  const [loyaltyForm, setLoyaltyForm] = useState({ phone: "" });
  const [loyaltyResult, setLoyaltyResult] = useState(null);
  const [rewardScreen, setRewardScreen] = useState(null); // { reward_value }

  useEffect(() => {
    document.title = "ZaykaQR — Menu";
    let cancelled = false;
    const load = async () => {
      try {
        // Record QR scan once per session
        const scanKey = `zq_scan_${slug}`;
        if (!sessionStorage.getItem(scanKey)) {
          api.post(`/public/scan/${slug}`).catch(() => {});
          sessionStorage.setItem(scanKey, "1");
        }
        const { data } = await api.get(`/public/menu/${slug}`);
        if (cancelled) return;
        if (data.suspended) {
          setData({ suspended: true });
        } else {
          setData(data);
          if (data.categories?.[0]) setActiveCat(data.categories[0].id);
          document.title = `${data.restaurant.name} — Menu | ZaykaQR`;
        }
      } catch (e) {
        setError("Restaurant not found");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  const items = data?.items || [];
  const cats = data?.categories || [];
  const restaurant = data?.restaurant;

  const filtered = useMemo(() => activeCat ? items.filter((i) => i.category_id === activeCat) : items, [items, activeCat]);

  const addToCart = (item) => {
    if (!item.available) return;
    setCart((c) => ({ ...c, [item.id]: (c[item.id] || 0) + 1 }));
    api.post(`/public/item-view/${slug}/${item.id}`).catch(() => {});
  };
  const dec = (id) => setCart((c) => {
    const n = (c[id] || 0) - 1;
    const next = { ...c };
    if (n <= 0) delete next[id];
    else next[id] = n;
    return next;
  });
  const inc = (id) => setCart((c) => ({ ...c, [id]: Math.min(99, (c[id] || 0) + 1) }));
  const removeFromCart = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const cartItems = Object.entries(cart).map(([id, qty]) => {
    const it = items.find((x) => x.id === id);
    return it ? { ...it, qty } : null;
  }).filter(Boolean);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.qty * Number(i.price || 0), 0);

  const placeOrderOnWhatsApp = async () => {
    if (!orderForm.name || !orderForm.phone) {
      toast.error("Enter name and phone to continue");
      return;
    }
    try {
      const payload = {
        customer_name: orderForm.name,
        customer_phone: orderForm.phone,
        items: cartItems.map((i) => ({ item_id: i.id, name: tx(i.name, lang, i.name?.en), qty: i.qty, price: Number(i.price) || 0 })),
        total: cartTotal,
      };
      const { data: orderRes } = await api.post(`/public/order/${slug}`, payload);

      // Build WA message
      const lines = cartItems.map((i) => `• ${tx(i.name, lang, i.name?.en)} × ${i.qty} — ₹${Number(i.price) * i.qty}`).join("\n");
      const msg =
`Hello! I want to place an order from ${restaurant.name} 🍽️

🛒 My Order:
${lines}

💰 Total: ₹${cartTotal}

👤 Name: ${orderForm.name}
📞 Phone: ${orderForm.phone}`;

      const num = (restaurant.whatsapp_number || "").replace(/\D/g, "");
      const wa = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
      window.open(wa, "_blank");

      // Reset cart
      setCart({});
      setOrderModal(false);
      setView("menu");

      // If reward unlocked → reward screen
      if (orderRes.reward_unlocked) {
        setRewardScreen({ value: orderRes.reward_value });
      } else {
        toast.success(`Order sent! +1 loyalty badge earned 🏅`);
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const submitFeedback = async () => {
    if (!feedbackForm.name || !feedbackForm.phone) return toast.error("Name and phone required");
    try {
      await api.post(`/public/feedback/${slug}`, feedbackForm);
      toast.success("Thanks for the feedback!");
      setFeedbackForm({ name: "", phone: "", rating: 5, message: "" });
      setView("menu");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const checkLoyalty = async () => {
    if (!loyaltyForm.phone) return;
    try {
      const { data } = await api.get(`/public/loyalty/${slug}`, { params: { phone: loyaltyForm.phone } });
      setLoyaltyResult(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading)
    return <div className="zq-mobile flex items-center justify-center"><div className="text-zq-muted py-20">Loading…</div></div>;

  if (error || !data)
    return (
      <div className="zq-mobile flex flex-col items-center justify-center text-center p-10">
        <AlertTriangle className="text-zq-orange" size={42}/>
        <h2 className="font-bold mt-4">Restaurant not found</h2>
        <p className="text-zq-muted text-sm mt-2">{error || "This menu does not exist."}</p>
      </div>
    );

  if (data.suspended)
    return (
      <div className="zq-mobile flex flex-col items-center justify-center text-center p-10 min-h-screen">
        <AlertTriangle className="text-zq-orange" size={42}/>
        <h2 className="font-bold mt-4">Menu temporarily unavailable</h2>
        <p className="text-zq-muted text-sm mt-2">Restaurant menu is temporarily unavailable. Please try again later.</p>
      </div>
    );

  // ------ Reward screen ------
  if (rewardScreen)
    return (
      <div className="zq-mobile p-6 flex flex-col items-center justify-center min-h-screen text-center" data-testid="reward-screen">
        <Award className="text-zq-orange" size={64} />
        <h1 className="text-3xl font-bold mt-4">🎉 Congratulations!</h1>
        <p className="text-zq-muted mt-2">You earned:</p>
        <div className="zq-pill zq-pill-orange text-base mt-3 px-5 py-2">{rewardScreen.value}</div>
        <p className="text-sm mt-4">Show this screen to restaurant staff to claim your reward.</p>
        <button className="zq-btn zq-btn-primary mt-8" onClick={() => setRewardScreen(null)} data-testid="reward-close">Back to menu</button>
      </div>
    );

  // ------ Cart Summary view ------
  if (view === "cart")
    return (
      <div className="zq-mobile pb-32" data-testid="cart-view">
        <div className="sticky top-0 bg-zq-cream z-10 p-4 border-b border-zq-border flex items-center gap-3">
          <button onClick={() => setView("menu")} data-testid="cart-back"><ArrowLeft size={20} /></button>
          <h2 className="font-bold text-lg">Your Order</h2>
        </div>

        <div className="p-4 space-y-3">
          {cartItems.length === 0 && (
            <div className="zq-card p-6 text-center text-zq-muted">
              Cart is empty.
              <button className="zq-btn zq-btn-outline mt-4 mx-auto" onClick={() => setView("menu")}>Add items</button>
            </div>
          )}
          {cartItems.map((i) => (
            <div key={i.id} className="zq-card p-3 flex gap-3 items-center" data-testid={`cart-item-${i.id}`}>
              {i.image_url && <img src={i.image_url} alt="" className="w-16 h-16 rounded-md object-cover"/>}
              <div className="flex-1">
                <div className="font-semibold text-sm">{tx(i.name, lang, i.name?.en)}</div>
                <div className="text-xs text-zq-muted">{inr(i.price)} each</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="zq-step">
                    <button onClick={() => dec(i.id)}><Minus size={14}/></button>
                    <span>{i.qty}</span>
                    <button onClick={() => inc(i.id)}><Plus size={14}/></button>
                  </div>
                  <div className="font-bold text-sm">{inr(i.price * i.qty)}</div>
                </div>
              </div>
              <button onClick={() => removeFromCart(i.id)} className="text-red-600" data-testid={`remove-${i.id}`}><Trash2 size={16}/></button>
            </div>
          ))}
        </div>

        {cartItems.length > 0 && (
          <div className="p-4 space-y-3">
            <div className="zq-card p-4 flex items-center justify-between">
              <span className="font-semibold">Subtotal</span>
              <span className="text-xl font-bold">{inr(cartTotal)}</span>
            </div>
            <button className="zq-btn zq-btn-outline w-full" onClick={() => setView("menu")} data-testid="add-more">Add more items</button>
            <button className="zq-btn zq-btn-whatsapp w-full" onClick={() => setOrderModal(true)} data-testid="order-whatsapp-btn">
              <MessageCircle size={18}/> Order on WhatsApp
            </button>
          </div>
        )}

        {/* Order details popup (also rendered in cart view so checkout works from cart) */}
        {orderModal && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" data-testid="order-modal">
            <div className="zq-card w-full max-w-sm p-6 bg-zq-cream relative">
              <button className="absolute top-3 right-3 text-zq-muted" onClick={() => setOrderModal(false)}><X size={18}/></button>
              <h3 className="text-lg font-bold">Enter your details</h3>
              <p className="text-xs text-zq-muted mt-1">Place order &amp; earn loyalty badges 📱</p>
              <input className="zq-input mt-4" placeholder="Full Name" value={orderForm.name} onChange={(e) => setOrderForm({ ...orderForm, name: e.target.value })} data-testid="order-name"/>
              <input className="zq-input mt-3" placeholder="Phone Number" value={orderForm.phone} onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })} data-testid="order-phone"/>
              <button className="zq-btn zq-btn-whatsapp w-full mt-4" onClick={placeOrderOnWhatsApp} data-testid="confirm-order">
                <MessageCircle size={18}/> Confirm &amp; Order on WhatsApp
              </button>
            </div>
          </div>
        )}
      </div>
    );

  // ------ Feedback view ------
  if (view === "feedback")
    return (
      <div className="zq-mobile pb-32" data-testid="feedback-view">
        <div className="sticky top-0 bg-zq-cream z-10 p-4 border-b border-zq-border flex items-center gap-3">
          <button onClick={() => setView("menu")}><ArrowLeft size={20} /></button>
          <h2 className="font-bold text-lg">Leave Feedback</h2>
        </div>
        <div className="p-4 space-y-3">
          <input className="zq-input" placeholder="Your name" value={feedbackForm.name} onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })} data-testid="fb-name"/>
          <input className="zq-input" placeholder="Phone" value={feedbackForm.phone} onChange={(e) => setFeedbackForm({ ...feedbackForm, phone: e.target.value })} data-testid="fb-phone"/>
          <div className="zq-card p-4">
            <div className="text-sm font-medium mb-2">Rating</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setFeedbackForm({ ...feedbackForm, rating: n })} data-testid={`fb-star-${n}`}>
                  <Star size={28} fill={n <= feedbackForm.rating ? "#F97316" : "transparent"} stroke="#F97316" />
                </button>
              ))}
            </div>
          </div>
          <textarea className="zq-input" rows={4} placeholder="Message (optional)" value={feedbackForm.message} onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })} data-testid="fb-msg"/>
          <button className="zq-btn zq-btn-primary w-full" onClick={submitFeedback} data-testid="fb-submit">Submit</button>
        </div>
      </div>
    );

  // ------ Loyalty view ------
  if (view === "loyalty")
    return (
      <div className="zq-mobile pb-32" data-testid="loyalty-view">
        <div className="sticky top-0 bg-zq-cream z-10 p-4 border-b border-zq-border flex items-center gap-3">
          <button onClick={() => { setView("menu"); setLoyaltyResult(null); }}><ArrowLeft size={20} /></button>
          <h2 className="font-bold text-lg">Loyalty Badges</h2>
        </div>
        <div className="p-4">
          <div className="zq-card p-5">
            <p className="text-sm">Enter your phone number to check your loyalty progress.</p>
            <input className="zq-input mt-3" placeholder="Phone number" value={loyaltyForm.phone} onChange={(e) => setLoyaltyForm({ phone: e.target.value })} data-testid="loy-phone-input"/>
            <button className="zq-btn zq-btn-primary w-full mt-3" onClick={checkLoyalty} data-testid="loy-check">Check progress</button>
          </div>
          {loyaltyResult && (
            <div className="zq-card p-5 mt-4 text-center" data-testid="loy-result">
              <p className="text-sm text-zq-muted">You have</p>
              <p className="text-4xl font-bold text-zq-orange mt-1">
                {loyaltyResult.loyalty.badges} / {loyaltyResult.config.target}
              </p>
              <p className="text-sm font-medium">badges 🏅</p>
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: loyaltyResult.config.target }).map((_, i) => (
                  <div key={i} className={i < loyaltyResult.loyalty.badges ? "zq-badge-filled" : "zq-badge-empty"}>
                    <Award size={18}/>
                  </div>
                ))}
              </div>
              {loyaltyResult.loyalty.badges >= loyaltyResult.config.target && loyaltyResult.config.enabled && (
                <div className="mt-5 p-4 rounded-lg bg-zq-orange-light border border-zq-orange">
                  <CheckCircle2 className="mx-auto text-zq-orange" size={28}/>
                  <p className="text-sm font-semibold mt-2">Reward unlocked!</p>
                  <p className="text-base mt-1">{loyaltyResult.config.reward_value}</p>
                  <p className="text-xs text-zq-muted mt-2">Show this screen to restaurant staff.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );

  // ------ Default Menu view ------
  return (
    <div className="zq-mobile pb-32" data-testid="customer-menu">
      {/* Header */}
      <div className="sticky top-0 bg-zq-cream z-10 border-b border-zq-border">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {restaurant.logo_url ? (
              <img src={restaurant.logo_url} alt="" className="w-11 h-11 rounded-full object-cover"/>
            ) : (
              <div className="w-11 h-11 rounded-full bg-zq-orange flex items-center justify-center text-white font-bold">
                {restaurant.name?.[0] || "Z"}
              </div>
            )}
            <div>
              <div className="font-bold text-base leading-tight">{restaurant.name}</div>
              {restaurant.address && <div className="text-[10px] text-zq-muted">{restaurant.address}</div>}
            </div>
          </div>
          <div className="flex gap-1">
            {LANGS.map((lg) => (
              <button
                key={lg.k}
                onClick={() => setLang(lg.k)}
                data-testid={`lang-${lg.k}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${lang === lg.k ? "bg-zq-orange text-white" : "bg-white text-zq-muted border border-zq-border"}`}
              >{lg.label}</button>
            ))}
          </div>
        </div>

        {/* Categories */}
        {cats.length > 0 && (
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto zq-scroll">
            {cats.map((c) => (
              <button
                key={c.id}
                className={`zq-pill whitespace-nowrap ${activeCat === c.id ? "zq-pill-orange" : "zq-pill-gray"}`}
                onClick={() => setActiveCat(c.id)}
                data-testid={`cust-cat-${c.id}`}
              >{c.name}</button>
            ))}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="p-4 space-y-3">
        {cats.length === 0 && (
          <div className="zq-card p-8 text-center text-zq-muted">Menu coming soon.</div>
        )}
        {filtered.map((it) => (
          <div key={it.id} className={`zq-card p-3 flex gap-3 ${it.available ? "" : "opacity-60"}`} data-testid={`menu-item-${it.id}`}>
            {it.image_url && <img src={it.image_url} alt={tx(it.name, lang)} className="w-20 h-20 rounded-lg object-cover" />}
            <div className="flex-1">
              <div className="font-semibold text-sm">{tx(it.name, lang, it.name?.en)}</div>
              {tx(it.description, lang) && <div className="text-xs text-zq-muted line-clamp-2 mt-1">{tx(it.description, lang)}</div>}
              <div className="flex items-center justify-between mt-2">
                <div className="font-bold">{inr(it.price)}</div>
                {!it.available ? (
                  <span className="zq-pill zq-pill-gray text-[10px]">Out of Stock</span>
                ) : cart[it.id] ? (
                  <div className="zq-step">
                    <button onClick={() => dec(it.id)} data-testid={`dec-${it.id}`}><Minus size={14}/></button>
                    <span>{cart[it.id]}</span>
                    <button onClick={() => inc(it.id)} data-testid={`inc-${it.id}`}><Plus size={14}/></button>
                  </div>
                ) : (
                  <button className="zq-btn zq-btn-primary !py-1.5 !px-3 text-xs" onClick={() => addToCart(it)} data-testid={`add-${it.id}`}>
                    <Plus size={14}/> Add
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action tiles */}
      <div className="p-4 space-y-3">
        <button className="zq-btn zq-btn-outline w-full justify-start" onClick={() => setView("loyalty")} data-testid="open-loyalty">
          <Award className="text-zq-orange" size={18} /> Check my loyalty badges
        </button>
        <button className="zq-btn zq-btn-outline w-full justify-start" onClick={() => setView("feedback")} data-testid="open-feedback">
          <Star className="text-zq-orange" size={18} /> Leave us feedback
        </button>
        {restaurant.google_review_link && (
          <a href={restaurant.google_review_link} target="_blank" rel="noreferrer" className="zq-btn zq-btn-outline w-full justify-start" data-testid="google-review">
            <ExternalLink size={18} className="text-zq-orange" /> Leave us a Google Review ⭐
          </a>
        )}
      </div>

      <div className="p-4 text-center text-[10px] text-zq-muted">
        <Languages size={12} className="inline mr-1"/> Powered by ZaykaQR · Desi flavour. Digital future.
      </div>

      {/* Floating cart bar */}
      {cartCount > 0 && (
        <button className="zq-cart-bar" onClick={() => setView("cart")} data-testid="floating-cart">
          <span className="flex items-center gap-2"><ShoppingCart size={16}/> {cartCount} item{cartCount > 1 ? "s" : ""} — {inr(cartTotal)}</span>
          <span className="font-semibold">View Cart →</span>
        </button>
      )}

      {/* Order details popup */}
      {orderModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" data-testid="order-modal">
          <div className="zq-card w-full max-w-sm p-6 bg-zq-cream relative">
            <button className="absolute top-3 right-3 text-zq-muted" onClick={() => setOrderModal(false)}><X size={18}/></button>
            <h3 className="text-lg font-bold">Enter your details</h3>
            <p className="text-xs text-zq-muted mt-1">Place order & earn loyalty badges 📱</p>
            <input className="zq-input mt-4" placeholder="Full Name" value={orderForm.name} onChange={(e) => setOrderForm({ ...orderForm, name: e.target.value })} data-testid="order-name"/>
            <input className="zq-input mt-3" placeholder="Phone Number" value={orderForm.phone} onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })} data-testid="order-phone"/>
            <button className="zq-btn zq-btn-whatsapp w-full mt-4" onClick={placeOrderOnWhatsApp} data-testid="confirm-order">
              <MessageCircle size={18}/> Confirm & Order on WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
